import IAction from "../store/IAction";
import IInvoiceVm from "../models/home/Payment/IInvoiceVm";
import InitState from "../store/initState";
import { InvoiceStatusType } from "../models/enums/InvoiceStatusType";
import ICompanyVm from "../models/shared/ICompanyVm";
import { companyReducer } from "./companiesReducer";
import { Dispatch } from "redux";
import Api from "../services/api";
import IBulkInvoiceSearchVm from "../models/shared/IBulkInvoiceSearchVm";
import * as metaReducer from "./metaReducer";
import { IBulkInvoiceSearchObject, ISortObject } from "./metaReducer";
import IApplicationBaseSearchVm from "../models/shared/IApplicationBaseSearchVm";
import { BulkPaymentDateSearchType } from "../models/enums/BulkPaymentDateSearchType";
import { Guid } from "guid-typescript";

const RELOAD = "BAT2/invoice/RELOAD";
const UPDATE_INVOICE_STATUS = "BAT2/invoice/UPDATE_STATUS";
const UPDATE_TERMS = "BAT2/invoice/UPDATE_TERMS";
const REPLACE_COMPANY = "BAT2/invoice/REPLACE_COMPANY";

export default function invoicesReducer(state: IInvoiceVm[] = [new InitState().invoice], action: IAction): IInvoiceVm[] {
  switch (action.type) {
    default:
      {
        if (state && state.length) {
          return [...state].map((invoice: IInvoiceVm) => {
            return invoiceReducer(invoice, action);
          });
        }
      }
      return state;
  }
}
export function invoiceReducer(state: IInvoiceVm = new InitState().invoice, action: IAction): IInvoiceVm {
  if (state.id === action.id) {
    switch (action.type) {
      case UPDATE_TERMS:
        return { ...state, areTermsAndConditionsAccepted: action.payload };
      case UPDATE_INVOICE_STATUS:
        return { ...state, status: action.payload };
      case RELOAD:
        return { ...action.payload };
      case REPLACE_COMPANY:
        return { ...state, companyBilled: action.payload };
      default:
        return {
          ...state,
          companyBilled: companyReducer(state.companyBilled, action),
        };
    }
  } else {
    return {
      ...state,
      companyBilled: companyReducer(state.companyBilled, action),
    };
  }
}

export function updateTermsAndConditionsAccepted(areTermsAndConditionsAccepted: boolean, id: string) {
  return { type: UPDATE_TERMS, payload: areTermsAndConditionsAccepted, id };
}
export function updateInvoiceStatus(value: InvoiceStatusType, id: string) {
  return { type: UPDATE_INVOICE_STATUS, payload: value, id };
}

export function replaceCompanyBilled(value: ICompanyVm, id: string) {
  return { type: REPLACE_COMPANY, payload: value, id };
}

// When search button is pressed, and on page loaded
export function updateBulkInvoiceSearchListAndSearch(bulkInvoiceSearchObject: IBulkInvoiceSearchObject) {
  return (dispatch: Dispatch<any>, getState: () => InitState) => {
    dispatch(metaReducer.setAsyncRunning(true)); // "Manually" set AsyncRunning
    dispatch(metaReducer.updateBulkInvoiceListPageNumber(1)); // Updating pageNumber to 1, to always to be sure to land on the first page, when searching.

    const bulkInvoiceSearchListPageNumber = getState().meta.bulkInvoiceSearchListPageNumber;
    const bulkInvoiceSearchSort = getState().meta.bulkInvoiceSearchSort;

    dispatch(metaReducer.updateBulkInvoiceSearchObject(bulkInvoiceSearchObject));

    try {
      searchBulkInvoicesAndApplicationsForBulkPayment(dispatch, bulkInvoiceSearchObject, bulkInvoiceSearchSort, bulkInvoiceSearchListPageNumber).then(() => {
        // Wating for callback, to be sure that both calls to api has finished, before setting AsyncRunning to false
        dispatch(metaReducer.setAsyncRunning(false));
      });
    } catch (e) {
      console.log(e);
    }
  };
}

/**
 * Method that search for both bulkInvoices and applicationsForBulkPayment, and then returns a callback when all is done
 * @param dispatch
 * @param bulkInvoiceSearchObject
 * @param bulkInvoiceSearchSort
 * @param bulkInvoiceSearchListPageNumber
 */
const searchBulkInvoicesAndApplicationsForBulkPayment = (dispatch: Dispatch<any>, bulkInvoiceSearchObject: IBulkInvoiceSearchObject, bulkInvoiceSearchSort: ISortObject, bulkInvoiceSearchListPageNumber: number) =>
  new Promise<void>(async (resolve) => {
    let bulkInvoiceSearchResult: IBulkInvoiceSearchVm = { parentBulkInvoiceList: [], count: 0 };
    let applicationsForBulkPaymentsSearchResult: IApplicationBaseSearchVm = { applicationBaseList: [], count: 0 };

    try {
      bulkInvoiceSearchResult = await Api.educationalInstitution.searchBulkInvoices(bulkInvoiceSearchSort.sortPropertyName, bulkInvoiceSearchSort.sortOrderASC, bulkInvoiceSearchListPageNumber, bulkInvoiceSearchObject, false);

      // If invoiceNumber, invoiceStatus or bulkPaymentDateSearchType is sat, we dont search for applicationsForBulkPayments, because that is specific for bulkInvoices.
      if (
        bulkInvoiceSearchObject.invoiceNumber === "" &&
        // bulkInvoiceSearchObject.invoiceStatus === InvoiceStatusType.NotSet &&
        bulkInvoiceSearchObject.bulkPaymentDateSearchType === BulkPaymentDateSearchType.NotSet
      ) {
        applicationsForBulkPaymentsSearchResult = await Api.educationalInstitution.searchApplicationsForBulkPayments(bulkInvoiceSearchSort.sortPropertyName, bulkInvoiceSearchSort.sortOrderASC, bulkInvoiceSearchListPageNumber, bulkInvoiceSearchObject, false);
      }

      // Replace
      dispatch(metaReducer.replaceBulkInvoices(bulkInvoiceSearchResult.parentBulkInvoiceList));
      dispatch(metaReducer.replaceApplicationsForBulkPayments(applicationsForBulkPaymentsSearchResult.applicationBaseList));

      dispatch(metaReducer.updateBulkInvoiceSearchTotalRowsCount(bulkInvoiceSearchResult.count));
      dispatch(metaReducer.updateApplicationsForBulkPaymentsSearchTotalRowsCount(applicationsForBulkPaymentsSearchResult.count));
    } catch (error) {
      console.log(error);
    } finally {
      resolve();
    }
  });

// When change page is pressed for bulk invoices
export function updateBulkInvoiceSearchPageNumberAndSearch(bulkInvoiceSearchListPageNumber: number) {
  return (dispatch: Dispatch<any>, getState: () => InitState) => {
    const bulkInvoiceSearchObject = getState().meta.bulkInvoiceSearchObject;
    const bulkInvoiceSearchSort = getState().meta.bulkInvoiceSearchSort;

    dispatch(metaReducer.updateBulkInvoiceListPageNumber(bulkInvoiceSearchListPageNumber));
    dispatch(searchBulkInvoices(bulkInvoiceSearchObject, bulkInvoiceSearchSort, bulkInvoiceSearchListPageNumber, true, false));
  };
}

// When change page is pressed for applicationsForBulkPayment
export function updateApplicationsForBulkPaymentSearchPageNumberAndSearch(applicationsForBulkPaymentsSearchListPageNumber: number) {
  return async (dispatch: Dispatch<any>, getState: () => InitState) => {
    const bulkInvoiceSearchObject = getState().meta.bulkInvoiceSearchObject;
    const bulkInvoiceSearchSort = getState().meta.bulkInvoiceSearchSort;

    dispatch(metaReducer.updateApplicationSearchListPageNumber(applicationsForBulkPaymentsSearchListPageNumber));
    dispatch(searchApplicationsForBulkPayments(bulkInvoiceSearchObject, bulkInvoiceSearchSort, applicationsForBulkPaymentsSearchListPageNumber, true, false));
  };
}

function searchBulkInvoices(bulkInvoiceSearchObject: IBulkInvoiceSearchObject, bulkInvoiceSearchSort: ISortObject, bulkInvoiceSearchListPageNumber: number, useAsyncMarker: boolean, keepSpinnerRunning: boolean) {
  return async (dispatch: Dispatch<any>, getState: () => InitState) => {
    let bulkInvoiceSearchResult: IBulkInvoiceSearchVm;
    try {
      bulkInvoiceSearchResult = await Api.educationalInstitution.searchBulkInvoices(bulkInvoiceSearchSort.sortPropertyName, bulkInvoiceSearchSort.sortOrderASC, bulkInvoiceSearchListPageNumber, bulkInvoiceSearchObject, useAsyncMarker);

      // Replace
      dispatch(metaReducer.replaceBulkInvoices(bulkInvoiceSearchResult.parentBulkInvoiceList));
      dispatch(metaReducer.updateBulkInvoiceSearchTotalRowsCount(bulkInvoiceSearchResult.count));
    } catch (error) {
      console.log(error);
    }
  };
}

function searchApplicationsForBulkPayments(bulkInvoiceSearchObject: IBulkInvoiceSearchObject, bulkInvoiceSearchSort: ISortObject, bulkInvoiceSearchListPageNumber: number, useAsyncMarker: boolean, keepSpinnerRunning: boolean) {
  return async (dispatch: Dispatch<any>) => {
    let applicationsForBulkPaymentsSearchResult: IApplicationBaseSearchVm;
    try {
      applicationsForBulkPaymentsSearchResult = await Api.educationalInstitution.searchApplicationsForBulkPayments(bulkInvoiceSearchSort.sortPropertyName, bulkInvoiceSearchSort.sortOrderASC, bulkInvoiceSearchListPageNumber, bulkInvoiceSearchObject, useAsyncMarker);

      // Replace
      dispatch(metaReducer.replaceApplicationsForBulkPayments(applicationsForBulkPaymentsSearchResult.applicationBaseList));
      dispatch(metaReducer.updateApplicationsForBulkPaymentsSearchTotalRowsCount(applicationsForBulkPaymentsSearchResult.count));
    } catch (error) {
      console.log(error);
    }
  };
}

export function issueBulkPaymentDemand(selectedBulkPaymentId: string) {
  return async (dispatch: Dispatch<any>) => {
    const result = await Api.shared.issueBulkPaymentDemand(selectedBulkPaymentId);

    if (Guid.isGuid(result.id)) {
      dispatch(metaReducer.addToastr({ message: "Betalingskrav udstedt", type: "success" }));
      dispatch(metaReducer.replaceBulkInvoice(result));
    }
  };
}
